if (!customElements.get('bra-size-mapper')) {
  class BraSizeMapper extends HTMLElement {
    constructor() {
      super();
      this.mappingConfig = null;
      this.fitLabels = {};
      this.productSizes = [];
      this.isSyncing = false;
    }

    connectedCallback() {
      this.bandSelect = this.querySelector('[data-bra-band]');
      this.cupSelect = this.querySelector('[data-bra-cup]');
      this.sizeSelect = this.querySelector('[data-bra-size]');
      this.hintEl = this.querySelector('[data-bra-hint]');
      this.errorEl = this.querySelector('[data-bra-error]');
      this.sizeUnavailableEl = this.querySelector('[data-bra-size-unavailable]');
      const sizesPayload = this.dataset.productSizes;
      if (sizesPayload) {
        try {
          this.productSizes = JSON.parse(sizesPayload);
        } catch (e) {
          this.productSizes = [];
        }
      }

      this.variantRadios = this.closest('variant-radios');
      this.hiddenFieldset =
        this.variantRadios?.querySelector(`[data-bra-hidden-sizes="${this.dataset.sectionId}"]`) || null;

      this.strings = {
        selectSize: this.dataset.selectSize || 'Select size',
        fitsHtml: this.dataset.fitsHtml || 'Size {{ size }} fits {{ fits }}',
        sizeUnavailable: this.dataset.sizeUnavailable || 'Size Unavailable',
        comboUnavailableHtml:
          this.dataset.comboUnavailableHtml ||
          'Size {{ combo }} is not available. Please select a different size.',
      };

      this.bandSelect?.addEventListener('change', () => this.onBandCupChange());
      this.cupSelect?.addEventListener('change', () => this.onBandCupChange());
      this.sizeSelect?.addEventListener('change', () => this.onSizeDirectChange());

      this.loadMapping().then(() => {
        this.hideUnavailableState();
        this.updateSizeOptionStates('', '');
        if (this.sizeSelect?.value) {
          this.syncHiddenSize(this.sizeSelect.value);
        } else {
          this.setPendingSelection();
        }
      });
    }

    async loadMapping() {
      const mappingUrl = this.dataset.mappingUrl;
      if (!mappingUrl) return;

      try {
        const response = await fetch(mappingUrl);
        this.mappingConfig = await response.json();
        this.fitLabels = this.buildFitLabels(this.mappingConfig.map || {});
        this.applyFitLabelAliases();
        this.populateBandCupSelects();
      } catch (e) {
        this.mappingConfig = { bands: [], cups: [], map: {} };
        this.fitLabels = {};
      }
    }

    populateBandCupSelects() {
      if (!this.mappingConfig) return;

      if (this.bandSelect && this.bandSelect.options.length <= 1) {
        (this.mappingConfig.bands || []).forEach((band) => {
          const option = document.createElement('option');
          option.value = String(band);
          option.textContent = String(band);
          this.bandSelect.appendChild(option);
        });
      }

      if (this.cupSelect && this.cupSelect.options.length <= 1) {
        (this.mappingConfig.cups || []).forEach((cup) => {
          const option = document.createElement('option');
          option.value = cup;
          option.textContent = cup;
          this.cupSelect.appendChild(option);
        });
      }
    }

    buildFitLabels(map) {
      const fitLabels = {};
      Object.keys(map).forEach((cup) => {
        Object.keys(map[cup] || {}).forEach((band) => {
          const label = map[cup][band];
          if (!label) return;
          fitLabels[label] = fitLabels[label] || [];
          fitLabels[label].push(`${band}${cup}`);
        });
      });
      Object.keys(fitLabels).forEach((label) => {
        fitLabels[label].sort((a, b) => {
          const bandA = parseInt(a, 10);
          const bandB = parseInt(b, 10);
          if (bandA !== bandB) return bandA - bandB;
          return a.localeCompare(b);
        });
      });
      return fitLabels;
    }

    normalizeSize(value) {
      return String(value || '').trim();
    }

    normalizeSizeAlias(value) {
      return this.normalizeSize(value).toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    resolveProductSize(mappedLabel) {
      const label = this.normalizeSize(mappedLabel);
      if (!label) return null;

      const direct = this.productSizes.find((size) => this.normalizeSize(size) === label);
      if (direct) return direct;

      const alias = this.normalizeSizeAlias(label);
      return (
        this.productSizes.find((size) => this.normalizeSizeAlias(size) === alias) || null
      );
    }

    applyFitLabelAliases() {
      Object.keys({ ...this.fitLabels }).forEach((label) => {
        const resolved = this.resolveProductSize(label);
        if (resolved) {
          this.fitLabels[resolved] = this.fitLabels[label];
        }
      });
    }

    lookupMappedLabel(band, cup) {
      if (!this.mappingConfig?.map || !band || !cup) return null;
      return this.mappingConfig.map[cup]?.[String(band)] || null;
    }

    resolveBandCupSize(band, cup) {
      return this.resolveProductSize(this.lookupMappedLabel(band, cup));
    }

    formatBandCupCombo(band, cup) {
      return `${band}${cup}`;
    }

    getEligibleSizes(band, cup) {
      const eligible = new Set();
      const map = this.mappingConfig?.map || {};

      if (!band && !cup) {
        this.productSizes.forEach((size) => eligible.add(size));
        return eligible;
      }

      if (band && cup) {
        const resolved = this.resolveProductSize(this.lookupMappedLabel(band, cup));
        if (resolved) eligible.add(resolved);
        return eligible;
      }

      if (band) {
        Object.keys(map).forEach((cupKey) => {
          const resolved = this.resolveProductSize(map[cupKey]?.[String(band)]);
          if (resolved) eligible.add(resolved);
        });
        return eligible;
      }

      Object.keys(map[cup] || {}).forEach((bandKey) => {
        const resolved = this.resolveProductSize(map[cup][bandKey]);
        if (resolved) eligible.add(resolved);
      });
      return eligible;
    }

    updateSizeOptionStates(band, cup) {
      if (!this.sizeSelect) return;

      const eligibleSizes = this.getEligibleSizes(band, cup);

      Array.from(this.sizeSelect.options).forEach((option) => {
        if (!option.value) return;
        option.disabled = !eligibleSizes.has(option.value);
      });

      const currentValue = this.sizeSelect.value;
      if (currentValue) {
        const currentOption = Array.from(this.sizeSelect.options).find(
          (option) => option.value === currentValue
        );
        if (!currentOption || currentOption.disabled) {
          this.isSyncing = true;
          this.sizeSelect.value = '';
          this.isSyncing = false;
        }
      }
    }

    clearSizeSelection() {
      this.isSyncing = true;
      if (this.sizeSelect) this.sizeSelect.value = '';
      this.isSyncing = false;
      this.hideUnavailableState();
      this.setPendingSelection();
      this.hideHint();
    }

    showUnavailableState(band, cup) {
      this.isSyncing = true;
      if (this.sizeSelect) {
        this.sizeSelect.value = '';
        this.sizeSelect.classList.add('is-hidden');
      }
      this.isSyncing = false;

      if (this.sizeUnavailableEl) {
        this.sizeUnavailableEl.hidden = false;
        this.sizeUnavailableEl.textContent = this.strings.sizeUnavailable;
      }

      if (this.errorEl) {
        const combo = this.formatBandCupCombo(band, cup);
        this.errorEl.textContent = this.strings.comboUnavailableHtml.replace(
          /\{\{\s*combo\s*\}\}/g,
          combo
        );
        this.errorEl.classList.add('is-visible');
      }

      this.hideHint();
      this.setPendingSelection();
    }

    hideUnavailableState() {
      if (this.sizeSelect) this.sizeSelect.classList.remove('is-hidden');
      if (this.sizeUnavailableEl) this.sizeUnavailableEl.hidden = true;
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.classList.remove('is-visible');
      }
    }

    selectFirstEligibleSize(band, cup) {
      const productSize = this.resolveBandCupSize(band, cup);

      if (!productSize) {
        this.showUnavailableState(band, cup);
        return;
      }

      this.hideUnavailableState();

      this.isSyncing = true;
      this.sizeSelect.value = productSize;
      this.isSyncing = false;
      this.syncHiddenSize(productSize);
      this.showHint(productSize);
    }

    onBandCupChange() {
      if (this.isSyncing) return;

      const band = this.bandSelect?.value || '';
      const cup = this.cupSelect?.value || '';

      if (!band && !cup) {
        this.resetUi();
        this.setPendingSelection();
        return;
      }

      if (!band || !cup) {
        this.updateSizeOptionStates(band, cup);
        this.clearSizeSelection();
        return;
      }

      this.updateSizeOptionStates(band, cup);
      this.selectFirstEligibleSize(band, cup);
    }

    onSizeDirectChange() {
      if (this.isSyncing) return;

      const size = this.sizeSelect?.value || '';

      this.isSyncing = true;
      this.bandSelect.value = '';
      this.cupSelect.value = '';
      this.isSyncing = false;

      this.updateSizeOptionStates('', '');

      if (!size) {
        this.clearSizeSelection();
        return;
      }

      this.hideUnavailableState();
      this.syncHiddenSize(size);
      this.showHint(size);
    }

    syncHiddenSize(sizeValue) {
      const radios = this.getHiddenSizeRadios();
      let matched = false;

      radios.forEach((radio) => {
        const isMatch = this.normalizeSize(radio.value) === this.normalizeSize(sizeValue);
        radio.checked = isMatch;
        if (isMatch) matched = true;
      });

      if (!matched) {
        this.setPendingSelection();
        return;
      }

      this.variantRadios?.removeAttribute('data-bra-size-pending');
      this.disableVariantInput(false);

      const checked = radios.find((radio) => radio.checked);
      if (checked) {
        checked.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    getHiddenSizeRadios() {
      if (!this.hiddenFieldset) return [];
      return Array.from(this.hiddenFieldset.querySelectorAll('input[type="radio"]'));
    }

    clearHiddenSizeSelection() {
      this.getHiddenSizeRadios().forEach((radio) => {
        radio.checked = false;
      });
    }

    setPendingSelection() {
      this.variantRadios?.setAttribute('data-bra-size-pending', 'true');
      this.updateAddButton(true, this.strings.selectSize);
    }

    resetUi() {
      this.isSyncing = true;
      this.bandSelect.value = '';
      this.cupSelect.value = '';
      this.sizeSelect.value = '';
      this.isSyncing = false;
      this.hideUnavailableState();
      this.updateSizeOptionStates('', '');
      this.hideHint();
    }

    showHint(sizeValue) {
      if (!this.hintEl) return;
      const fits = (this.fitLabels[sizeValue] || []).join(', ');
      if (!fits) {
        this.hideHint();
        return;
      }
      const text = this.strings.fitsHtml
        .replace(/\{\{\s*size\s*\}\}/g, sizeValue)
        .replace(/\{\{\s*fits\s*\}\}/g, fits);
      this.hintEl.textContent = text;
      this.hintEl.classList.add('is-visible');
    }

    hideHint() {
      if (!this.hintEl) return;
      this.hintEl.textContent = '';
      this.hintEl.classList.remove('is-visible');
    }

    getProductForm() {
      const formId = this.dataset.productFormId;
      if (formId) {
        return document.getElementById(formId);
      }
      return this.variantRadios?.closest('section')?.querySelector('product-form form');
    }

    disableVariantInput(disabled) {
      const form = this.getProductForm();
      const input = form?.querySelector('input[name="id"]');
      if (!input) return;
      if (disabled) {
        input.setAttribute('disabled', 'disabled');
        input.value = '';
      } else {
        input.removeAttribute('disabled');
      }
    }

    updateAddButton(disabled, text) {
      if (this.variantRadios?.toggleAddButton) {
        this.variantRadios.toggleAddButton(disabled, text || '', false);
        return;
      }

      const form = this.getProductForm();
      const addButton = form?.querySelector('[name="add"]');
      const addButtonText = form?.querySelector('[name="add"] > span');
      if (!addButton || !addButtonText) return;

      if (disabled) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      }
    }
  }

  customElements.define('bra-size-mapper', BraSizeMapper);
}
